/**
 * Claim Dialog Component
 * Displays claim details for insurance payment sessions
 */

import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  capitalize,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material'
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Info as InfoIcon,
  Pending as PendingIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material'
import dayjs, { Dayjs } from 'dayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { capitalizeName } from '@/utils/validation'
import { membershipApi } from '@/services/api'
import { useDebounce } from '@/hooks/useDebounce'
import Autocomplete from '@mui/material/Autocomplete'

// Helper function to format modality strings (e.g., "in_person" -> "In Person")
const formatModality = (modality: string | undefined | null): string => {
  if (!modality) return 'In-Person'

  // Replace underscores with spaces and capitalize each word
  return modality
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Claim status config for chip (matches ClaimsPage)
const getStatusConfig = (
  status: string
): { color: string; label: string; icon: React.ReactElement | undefined } => {
  switch (status.toLowerCase()) {
    case 'pending':
      return {
        color: 'warning',
        label: 'Pending',
        icon: <PendingIcon fontSize="small" />,
      }
    case 'submitted':
      return {
        color: '#17a2b8',
        label: 'Submitted',
        icon: <SendIcon fontSize="small" />,
      }
    case 'accepted':
      return {
        color: 'info',
        label: 'Accepted',
        icon: <CheckCircleIcon fontSize="small" />,
      }
    case 'rejected':
      return {
        color: 'error',
        label: 'Rejected',
        icon: <CancelIcon fontSize="small" />,
      }
    case 'paid':
      return {
        color: 'success',
        label: 'Paid',
        icon: <PaymentIcon fontSize="small" />,
      }
    default:
      return {
        color: 'default',
        label: capitalize(status) || status,
        icon: undefined,
      }
  }
}

interface ClaimData {
  _id?: string
  claimId?: string
  sessionId?: string
  sessionIds?: string
  payerName?: string
  payerId?: string
  serviceName?: string
  submissionDate?: string
  claimedAmount?: number
  coPayAmount?: number
  patientFirstName?: string
  patientLastName?: string
  patientDob?: string
  patientGender?: string
  patientAddress?: string
  patientState?: string
  patientCity?: string
  patientZip?: string
  insuredFirstName?: string
  insuredLastName?: string
  insuredDob?: string
  insuredGender?: string
  insuredAddress?: string
  insuredState?: string
  insuredCity?: string
  insuredZip?: string
  diagnosisCode?: string | null
  diagnosisCodes?: string[]
  procedures?: string[]
  serviceStartDate?: string
  serviceEndDate?: string
  serviceFromDate?: string
  serviceToDate?: string
  pos?: string | null
  emg?: string | null
  procedure?: string | null
  diagRef?: string | null
  prior_auth_number?: string | null
  renderTaxonomy?: string | null
  charge?: number
  sessionNo?: number
  tax_id?: string
  total_charges?: number
  amount_paid?: number
  balance?: number
  rProvFistName?: string
  rProvLastName?: string
  rPhone?: string
  rAddress?: string
  rState?: string
  rCity?: string
  rZip?: string
  rNpi?: string
  rBillid?: string | null
  bProvFistName?: string
  bProvLastName?: string
  bPhone?: string
  bAddress?: string
  bState?: string
  bCity?: string
  bZip?: string
  bNpi?: string
  bBillid?: string | null
  status?: string
  claim_status?: string
  claimMdMessages?: string[]
  paid_date?: string
  claimmd_prov_name?: string
  check_number?: string
  [key: string]: any
}

interface ClaimDialogProps {
  open: boolean
  onClose: () => void
  claimData: ClaimData | null
  loading?: boolean
  readOnly?: boolean
  isResubmit?: boolean
  showStatusChip?: boolean
  onGenerateClaim?: (data: any) => void
}

const ClaimDialog: React.FC<ClaimDialogProps> = ({
  open,
  onClose,
  claimData,
  loading = false,
  readOnly = false,
  isResubmit = false,
  showStatusChip = true,
  onGenerateClaim,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    payerName: '',
    payerId: '',
    patientFirstName: '',
    patientLastName: '',
    patientDob: '',
    patientGender: '',
    patientAddress: '',
    patientState: '',
    patientCity: '',
    patientZip: '',
    insuredFirstName: '',
    insuredLastName: '',
    insuredDob: '',
    insuredGender: '',
    insuredAddress: '',
    insuredState: '',
    insuredCity: '',
    insuredZip: '',
    diagnosisCodes: Array(12).fill(''),
    serviceStartDate: '',
    serviceEndDate: '',
    pos: '',
    emg: '',
    procedure: '',
    diagRef: '',
    charge: 0,
    sessionNo: 0,
    tax_id: '',
    total_charges: 0,
    amount_paid: 0,
    balance: 0,
    rProvFistName: '',
    rProvLastName: '',
    rPhone: '',
    rAddress: '',
    rState: '',
    rCity: '',
    rZip: '',
    rNpi: '',
    rBillid: '',
    bProvFistName: '',
    bProvLastName: '',
    bPhone: '',
    bAddress: '',
    bState: '',
    bCity: '',
    bZip: '',
    bNpi: '',
    bBillid: '',
    prior_auth_number: '',
    renderTaxonomy: '',
    procedures: [] as string[],
  })
  const [generating, setGenerating] = useState(false)
  console.log('claimData', claimData)
  // Payer search state
  const [payerSearchInput, setPayerSearchInput] = useState('')
  const [payerSearchOptions, setPayerSearchOptions] = useState<
    Array<{
      _id: string
      payer_name: string
      payerid: string
    }>
  >([])
  const [selectedPayer, setSelectedPayer] = useState<{
    _id: string
    payer_name: string
    payerid: string
  } | null>(null)
  const [isSearchingPayers, setIsSearchingPayers] = useState(false)
  const debouncedPayerSearch = useDebounce(payerSearchInput, 300)

  // Search payers when debounced input changes
  useEffect(() => {
    const searchPayers = async () => {
      if (debouncedPayerSearch.trim().length === 0) {
        setPayerSearchOptions([])
        setIsSearchingPayers(false)
        return
      }

      setIsSearchingPayers(true)

      try {
        const response = await membershipApi.searchPayers(debouncedPayerSearch)
        const payersData = response.data || response
        if (Array.isArray(payersData)) {
          setPayerSearchOptions(payersData)
        }
      } catch (error) {
        console.error('Error searching payers:', error)
      } finally {
        setIsSearchingPayers(false)
      }
    }

    searchPayers()
  }, [debouncedPayerSearch])

  React.useEffect(() => {
    if (claimData) {
      // Initialize diagnosis codes - handle both array and single value
      const diagnosisCodesArray = Array(12).fill('')
      if (claimData.diagnosisCodes && Array.isArray(claimData.diagnosisCodes)) {
        claimData.diagnosisCodes.forEach((code, index) => {
          if (index < 12) {
            diagnosisCodesArray[index] = code
          }
        })
      } else if (claimData.diagnosisCode) {
        diagnosisCodesArray[0] = claimData.diagnosisCode
      }

      // Initialize procedures - handle array and convert to comma-separated string
      const proceduresArray =
        claimData.procedures && Array.isArray(claimData.procedures)
          ? claimData.procedures
          : claimData.procedure
            ? [claimData.procedure]
            : []
      const proceduresString = proceduresArray.join(', ')

      // Handle service dates - prefer serviceFromDate/serviceToDate, fallback to serviceStartDate/serviceEndDate
      const serviceStart =
        claimData.serviceFromDate || claimData.serviceStartDate || ''
      const serviceEnd =
        claimData.serviceToDate || claimData.serviceEndDate || ''

      // Initialize selected payer if payerName exists
      let initialPayer = null
      if (claimData.payerName && claimData.payerId) {
        initialPayer = {
          _id: '',
          payer_name: claimData.payerName,
          payerid: claimData.payerId,
        }
        // Add to search options if not already there
        setPayerSearchOptions(prev => {
          const exists = prev.some(p => p.payer_name === claimData.payerName)
          if (!exists && initialPayer) {
            return [...prev, initialPayer]
          }
          return prev
        })
        // Set the input value to show the payer name
        setPayerSearchInput(claimData.payerName)
      } else {
        // Clear input if no payer
        setPayerSearchInput('')
      }

      setFormData({
        payerName: claimData.payerName || '',
        payerId: claimData.payerId || '',
        patientFirstName: claimData.patientFirstName || '',
        patientLastName: claimData.patientLastName || '',
        patientDob: claimData.patientDob || '',
        patientGender: claimData.patientGender || '',
        patientAddress: claimData.patientAddress || '',
        patientState: claimData.patientState || '',
        patientCity: claimData.patientCity || '',
        patientZip: claimData.patientZip || '',
        insuredFirstName: claimData.insuredFirstName || '',
        insuredLastName: claimData.insuredLastName || '',
        insuredDob: claimData.insuredDob || '',
        insuredGender: claimData.insuredGender || '',
        insuredAddress: claimData.insuredAddress || '',
        insuredState: claimData.insuredState || '',
        insuredCity: claimData.insuredCity || '',
        insuredZip: claimData.insuredZip || '',
        diagnosisCodes: diagnosisCodesArray,
        procedures: proceduresArray,
        serviceStartDate: serviceStart,
        serviceEndDate: serviceEnd,
        pos: claimData.pos || '',
        emg: claimData.emg || '',
        procedure: proceduresString, // Store as comma-separated string for display/editing
        diagRef: claimData.diagRef || '',
        charge: claimData.charge || claimData.claimedAmount || 0,
        sessionNo: claimData.sessionNo || 0,
        tax_id: claimData.tax_id || '',
        total_charges: claimData.total_charges || claimData.claimedAmount || 0,
        amount_paid: claimData.amount_paid || claimData.coPayAmount || 0,
        balance:
          claimData.balance ||
          (claimData.claimedAmount || 0) - (claimData.coPayAmount || 0),
        rProvFistName: claimData.rProvFistName || '',
        rProvLastName: claimData.rProvLastName || '',
        rPhone: claimData.rPhone || '',
        rAddress: claimData.rAddress || '',
        rState: claimData.rState || '',
        rCity: claimData.rCity || '',
        rZip: claimData.rZip || '',
        rNpi: claimData.rNpi || '',
        rBillid: claimData.rBillid || '',
        bProvFistName: claimData.bProvFistName || '',
        bProvLastName: claimData.bProvLastName || '',
        bPhone: claimData.bPhone || '',
        bAddress: claimData.bAddress || '',
        bState: claimData.bState || '',
        bCity: claimData.bCity || '',
        bZip: claimData.bZip || '',
        bNpi: claimData.bNpi || '',
        bBillid: claimData.bBillid || '',
        prior_auth_number: claimData.prior_auth_number || '',
        renderTaxonomy: claimData.renderTaxonomy || '',
      })

      // Set selected payer
      if (initialPayer) {
        setSelectedPayer(initialPayer)
      }
    }
  }, [claimData])

  if (!claimData && !loading) {
    return null
  }

  const handleGenerateClaim = async () => {
    if (claimData && onGenerateClaim) {
      setGenerating(true)

      // Calculate balance
      const balance = formData.total_charges - formData.amount_paid

      // Format dates to ISO format with time component using dayjs
      const formatDateToISO = (
        dateValue?: string | Dayjs | null
      ): string | null => {
        if (!dateValue) return null
        try {
          // Parse the date string (MM/DD/YYYY format) or use dayjs object directly
          let date: Dayjs
          if (typeof dateValue === 'string' && dateValue.includes('/')) {
            // Parse MM/DD/YYYY format explicitly to avoid timezone issues
            const [month, day, year] = dateValue.split('/')
            // Create date in local timezone at midnight
            date = dayjs(
              `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
            )
          } else {
            date = dayjs(dateValue)
          }
          if (!date.isValid()) return null
          // Format as YYYY-MM-DDTHH:mm:ss.sssZ
          // Use format to create ISO string with time at midnight local time
          // Then convert to ISO string ensuring the date is preserved
          const year = date.year()
          const month = String(date.month() + 1).padStart(2, '0')
          const day = String(date.date()).padStart(2, '0')
          // Create ISO string with time at midnight UTC, but ensure date part matches selected date
          return `${year}-${month}-${day}T00:00:00.000Z`
        } catch {
          return null
        }
      }

      // Convert empty strings to null for optional fields
      const toNullIfEmpty = (
        value: string | null | undefined
      ): string | null => {
        if (value === null || value === undefined || value.trim() === '') {
          return null
        }
        return value
      }

      // Extract MongoDB _id from sessionId object if it exists
      // The sessionId in claimData can be either:
      // 1. An object with _id property (from API response)
      // 2. A MongoDB _id string (if passed directly)
      // 3. sessionIdMongoId (if set in SessionRow)
      let mongoSessionId = ''
      if (
        claimData.sessionId &&
        typeof claimData.sessionId === 'object' &&
        (claimData.sessionId as any)._id
      ) {
        // Extract _id from sessionId object
        mongoSessionId = (claimData.sessionId as any)._id
      } else if (claimData.sessionIdMongoId) {
        // Use the MongoDB _id that was explicitly set
        mongoSessionId = claimData.sessionIdMongoId
      } else if (
        claimData.sessionId &&
        typeof claimData.sessionId === 'string'
      ) {
        // If sessionId is already a string, use it (assuming it's MongoDB _id)
        mongoSessionId = claimData.sessionId
      } else if (claimData._id) {
        // Fallback to claim _id
        mongoSessionId = claimData._id
      }

      const payload = {
        sessionId: mongoSessionId,
        payerName: formData.payerName,
        serviceName: claimData.serviceName || '',
        submissionDate: claimData.submissionDate || new Date().toISOString(),
        claimedAmount: formData.total_charges,
        coPayAmount: formData.amount_paid,
        documentUrl: null,
        invoiceUrl: null,
        invoiceId: null,
        invoiceRaised: null,
        payerId: formData.payerId,
        patientFirstName: formData.patientFirstName,
        patientLastName: formData.patientLastName,
        patientDob: formatDateToISO(formData.patientDob),
        patientGender: formData.patientGender,
        patientAddress: formData.patientAddress,
        patientState: formData.patientState,
        patientCity: formData.patientCity,
        patientZip: formData.patientZip,
        insuredFirstName: formData.insuredFirstName,
        insuredLastName: formData.insuredLastName,
        insuredDob: formatDateToISO(formData.insuredDob),
        insuredGender: formData.insuredGender,
        insuredAddress: formData.insuredAddress,
        insuredState: formData.insuredState,
        insuredCity: formData.insuredCity,
        insuredZip: formData.insuredZip,
        diagnosisCodes: formData.diagnosisCodes.filter(
          code => code.trim() !== ''
        ),
        procedures: formData.procedure
          ? formData.procedure
            .split(',')
            .map(p => p.trim())
            .filter(p => p !== '')
          : [],
        serviceStartDate: formatDateToISO(formData.serviceStartDate),
        serviceEndDate: formatDateToISO(formData.serviceEndDate),
        pos: toNullIfEmpty(formData.pos),
        emg: toNullIfEmpty(formData.emg),
        diagRef: toNullIfEmpty(formData.diagRef),
        prior_auth_number: toNullIfEmpty(formData.prior_auth_number),
        renderTaxonomy: toNullIfEmpty(formData.renderTaxonomy),
        charge: formData.charge,
        sessionNo: formData.sessionNo,
        tax_id: formData.tax_id,
        total_charges: formData.total_charges,
        amount_paid: formData.amount_paid,
        balance: balance,
        rProvFistName: formData.rProvFistName,
        rProvLastName: formData.rProvLastName,
        rPhone: formData.rPhone,
        rAddress: formData.rAddress,
        rState: formData.rState,
        rCity: formData.rCity,
        rZip: formData.rZip,
        rNpi: formData.rNpi,
        rBillid: toNullIfEmpty(formData.rBillid),
        bProvFistName: formData.bProvFistName,
        bProvLastName: formData.bProvLastName,
        bPhone: formData.bPhone,
        bAddress: formData.bAddress,
        bState: formData.bState,
        bCity: formData.bCity,
        bZip: formData.bZip,
        bNpi: formData.bNpi,
        bBillid: toNullIfEmpty(formData.bBillid),
      }

      await onGenerateClaim(payload)
      setGenerating(false)
    }
  }

  const updateFormField = (field: string, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value }
      // Auto-calculate balance when total_charges or amount_paid changes
      if (field === 'total_charges' || field === 'amount_paid') {
        updated.balance = updated.total_charges - updated.amount_paid
      }
      return updated
    })
  }

  const updateDiagnosisCode = (index: number, value: string) => {
    setFormData(prev => {
      const newCodes = [...prev.diagnosisCodes]
      newCodes[index] = value
      return { ...prev, diagnosisCodes: newCodes }
    })
  }

  const patientName =
    `${claimData?.patientId?.firstName} ${claimData?.patientId?.lastName}`.trim() ||
    'N/A'
  const providerName =
    `${claimData?.therapistId?.firstName} ${claimData?.therapistId?.lastName}`.trim() ||
    'N/A'
  const billingProviderName =
    `${formData.bProvFistName} ${formData.bProvLastName}`.trim() || 'N/A'

  // Format date to MM/DD/YYYY using dayjs
  const formatDateForDisplay = (dateString?: string | Dayjs | null): string => {
    if (!dateString) return ''
    try {
      const date = dayjs(dateString)
      if (!date.isValid()) return ''
      return date.format('MM/DD/YYYY')
    } catch {
      return ''
    }
  }

  // Convert date string to dayjs object
  const parseDateToDayjs = (dateString?: string | null): Dayjs | null => {
    if (!dateString) return null
    try {
      const date = dayjs(dateString)
      return date.isValid() ? date : null
    } catch {
      return null
    }
  }

  const sessionDate = formData.serviceStartDate
    ? formatDateForDisplay(formData.serviceStartDate)
    : ''

  // Get duration from claimData or default to 60
  const duration = (() => {
    if (claimData?.contractId) {
      return (claimData.contractId as any).duretion || 60
    }
    return 60
  })()

  const rejectAndInvoiceHave = claimData?.status?.toLowerCase() === 'rejected' && claimData?.invoiceRaised === true;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DialogContent
          sx={{
            p: 0,
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            '&.MuiDialogContent-root': {
              overflow: 'hidden',
            },
          }}
          dividers={false}
        >
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="500px"
            >
              <CircularProgress size={60} />
            </Box>
          ) : (
            <Box sx={{ p: 4, overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {/* Header */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="start"
                mb={3}
              >
                <Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        color: '#1F2937',
                        fontSize: '1.75rem',
                      }}
                    >
                      Claim Details
                    </Typography>
                    {showStatusChip &&
                      (claimData?.status || claimData?.claim_status) &&
                      (() => {
                        const claimStatus =
                          claimData?.status || claimData?.claim_status || ''
                        const statusConfig = getStatusConfig(claimStatus)
                        const isCustomColor =
                          typeof statusConfig.color === 'string' &&
                          statusConfig.color.startsWith('#')
                        return (
                          <Chip
                            icon={statusConfig.icon}
                            label={statusConfig.label}
                            color={
                              isCustomColor
                                ? undefined
                                : (statusConfig.color as any)
                            }
                            size="small"
                            sx={{
                              fontWeight: 500,
                              borderRadius: '1rem',
                              ...(isCustomColor
                                ? {
                                  backgroundColor: statusConfig.color,
                                  color: 'white',
                                  '& .MuiChip-icon': { color: 'white' },
                                }
                                : {}),
                            }}
                          />
                        )
                      })()}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Review submitted claim information
                  </Typography>
                </Box>
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{
                    color: 'grey.500',
                    '&:hover': { color: 'grey.700' },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Info Alert */}
              {!readOnly && (
                <Alert
                  icon={<InfoIcon />}
                  severity="info"
                  sx={{
                    mb: 3,
                    backgroundColor: '#EFF6FF',
                    color: '#1E40AF',
                    '& .MuiAlert-icon': {
                      color: '#3B82F6',
                    },
                  }}
                >
                  This is an automated claim preview generated based on session
                  details. Please verify before confirmation.
                </Alert>
              )}

              {/* Claim Messages Alert - Show only for rejected status */}
              {(claimData?.status?.toLowerCase() === 'rejected' ||
                claimData?.claim_status?.toLowerCase() === 'rejected') &&
                claimData?.claimMdMessages &&
                Array.isArray(claimData.claimMdMessages) &&
                claimData.claimMdMessages.length > 0 && (
                  <Alert
                    icon={<InfoIcon />}
                    severity="warning"
                    sx={{
                      mb: 3,
                      backgroundColor: '#FFF7ED',
                      color: '#92400E',
                      '& .MuiAlert-icon': {
                        color: '#F59E0B',
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Important Claim Messages:
                    </Typography>
                    <List dense sx={{ p: 0, m: 0, listStyle: 'disc', pl: 2 }}>
                      {claimData.claimMdMessages.map(
                        (message: string, idx: number) => (
                          <ListItem
                            key={idx}
                            sx={{
                              p: 0,
                              py: 0.5,
                              alignItems: 'flex-start',
                              display: 'list-item',
                              listStyleType: 'disc',
                            }}
                          >
                            <ListItemText
                              primary={
                                typeof message === 'string'
                                  ? message
                                  : (message as any)?.message ||
                                  (message as any)?.mesgid ||
                                  'Unknown claim message'
                              }
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: {
                                  color: '#92400E',
                                  wordBreak: 'break-word',
                                },
                              }}
                            />
                          </ListItem>
                        )
                      )}
                    </List>
                  </Alert>
                )}

              {/* ID Details Section */}
              <Card
                sx={{
                  mb: 3,
                  backgroundColor: '#F9FAFB',
                  borderRadius: 2,
                  border: '1px solid #E5E7EB',
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  {/* <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: '#1F2937', mb: 2 }}
                >
                  ID Details
                </Typography> */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={0.5}
                      >
                        Claim ID:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {claimData?.claimId || claimData?._id || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={0.5}
                      >
                        Remote Claim ID:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(claimData as any)?.remote_claimid || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={0.5}
                      >
                        Contract ID:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {claimData?.contractId?.contract_ids || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={0.5}
                      >
                        Session ID:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {claimData?.sessionIds || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Session Summary */}
              <Card
                sx={{
                  mb: 3,
                  backgroundColor: '#F0F9FF',
                  borderRadius: 2,
                  border: '1px solid #E0F2FE',
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: '#1F2937', mb: 2 }}
                  >
                    Session Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={0.5}
                      >
                        Date of Session:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {sessionDate || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={0.5}
                      >
                        Duration:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {duration} mins
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={0.5}
                      >
                        Provider Name:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {providerName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={0.5}
                      >
                        Patient Name:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {patientName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={0.5}
                      >
                        Session Type:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {typeof claimData?.service_id === 'object' &&
                          claimData.service_id &&
                          (claimData.service_id as any).modality &&
                          Array.isArray((claimData.service_id as any).modality) &&
                          (claimData.service_id as any).modality.length > 0
                          ? formatModality(
                            (claimData.service_id as any).modality[0]
                          )
                          : 'In Person'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Form Sections */}
              <Box>
                {/* Section 1: Payer Name */}
                <Card sx={{ mb: 2, borderRadius: 2 }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2, color: '#1F2937' }}
                    >
                      1. Payer Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={payerSearchOptions}
                          value={selectedPayer}
                          inputValue={payerSearchInput}
                          onChange={(event, newValue) => {
                            if (newValue) {
                              setSelectedPayer(newValue)
                              setPayerSearchInput(newValue.payer_name) // Show selected payer name in input
                              updateFormField('payerName', newValue.payer_name)
                              updateFormField('payerId', newValue.payerid)
                            } else {
                              setSelectedPayer(null)
                              setPayerSearchInput('')
                              updateFormField('payerName', '')
                              updateFormField('payerId', '')
                            }
                          }}
                          onInputChange={(event, newInputValue, reason) => {
                            // Only update input when user is typing or clearing
                            // Don't update when an option is selected (handled in onChange)
                            if (reason === 'input') {
                              setPayerSearchInput(newInputValue)
                            } else if (reason === 'clear') {
                              setPayerSearchInput('')
                              setSelectedPayer(null)
                              updateFormField('payerName', '')
                              updateFormField('payerId', '')
                            } else if (reason === 'reset') {
                              // When reset, keep the selected payer name if available
                              if (selectedPayer) {
                                setPayerSearchInput(selectedPayer.payer_name)
                              } else {
                                setPayerSearchInput('')
                              }
                            }
                          }}
                          getOptionLabel={option => option.payer_name}
                          isOptionEqualToValue={(option, value) =>
                            option._id === value._id ||
                            option.payer_name === value.payer_name
                          }
                          loading={isSearchingPayers}
                          filterOptions={x => x}
                          disabled={readOnly}
                          noOptionsText={
                            payerSearchInput.trim().length === 0
                              ? 'Start typing to search for insurance providers'
                              : 'No insurance providers found'
                          }
                          onClose={(event, reason) => {
                            // When an option is selected, keep the payer name in the input
                            // The input value is already set in onChange handler
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label="Payer Name"
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                  '& fieldset': {
                                    borderColor: '#E5E7EB',
                                  },
                                },
                              }}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {isSearchingPayers ? (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Payer ID"
                          value={formData.payerId}
                          disabled={true}
                          InputProps={{
                            readOnly: true,
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#F3F4F6',
                              '& fieldset': {
                                borderColor: '#E5E7EB',
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Section 2: Patient Name */}
                <Card sx={{ mb: 2, borderRadius: 2 }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2, color: '#1F2937' }}
                    >
                      2. Patient Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="First Name"
                          value={formData.patientFirstName}
                          onChange={e =>
                            updateFormField('patientFirstName', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Last Name"
                          value={formData.patientLastName}
                          onChange={e =>
                            updateFormField('patientLastName', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DatePicker
                          label="Birthday"
                          value={parseDateToDayjs(formData.patientDob)}
                          onChange={newValue => {
                            updateFormField(
                              'patientDob',
                              newValue
                                ? dayjs(newValue).format('MM/DD/YYYY')
                                : ''
                            )
                          }}
                          format="MM/DD/YYYY"
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              disabled: readOnly,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Gender</InputLabel>
                          <Select
                            value={formData.patientGender}
                            label="Gender"
                            onChange={e =>
                              updateFormField('patientGender', e.target.value)
                            }
                            disabled={readOnly}
                          >
                            <MenuItem value="Male">Male</MenuItem>
                            <MenuItem value="Female">Female</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                            <MenuItem value="Prefer not to say">
                              Prefer not to say
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Patient Address"
                          value={formData.patientAddress}
                          onChange={e =>
                            updateFormField('patientAddress', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="State"
                          value={formData.patientState}
                          onChange={e =>
                            updateFormField('patientState', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="City"
                          value={formData.patientCity}
                          onChange={e =>
                            updateFormField('patientCity', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Zip Code"
                          value={formData.patientZip}
                          onChange={e =>
                            updateFormField('patientZip', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Section 3: Insured Name */}
                <Card sx={{ mb: 2, borderRadius: 2 }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2, color: '#1F2937' }}
                    >
                      3. Insured Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="First Name"
                          value={formData.insuredFirstName}
                          onChange={e =>
                            updateFormField('insuredFirstName', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Last Name"
                          value={formData.insuredLastName}
                          onChange={e =>
                            updateFormField('insuredLastName', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DatePicker
                          label="Birthday"
                          value={parseDateToDayjs(formData.insuredDob)}
                          onChange={newValue => {
                            updateFormField(
                              'insuredDob',
                              newValue
                                ? dayjs(newValue).format('MM/DD/YYYY')
                                : ''
                            )
                          }}
                          format="MM/DD/YYYY"
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              disabled: readOnly,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Gender</InputLabel>
                          <Select
                            value={formData.insuredGender}
                            label="Gender"
                            onChange={e =>
                              updateFormField('insuredGender', e.target.value)
                            }
                            disabled={readOnly}
                          >
                            <MenuItem value="Male">Male</MenuItem>
                            <MenuItem value="Female">Female</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                            <MenuItem value="Prefer not to say">
                              Prefer not to say
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Insured Address"
                          value={formData.insuredAddress}
                          onChange={e =>
                            updateFormField('insuredAddress', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="State"
                          value={formData.insuredState}
                          onChange={e =>
                            updateFormField('insuredState', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="City"
                          value={formData.insuredCity}
                          onChange={e =>
                            updateFormField('insuredCity', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Zip Code"
                          value={formData.insuredZip}
                          onChange={e =>
                            updateFormField('insuredZip', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Section 4: Diagnosis Codes */}
                <Card sx={{ mb: 2, borderRadius: 2 }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2, color: '#1F2937' }}
                    >
                      4. Diagnosis or Nature of Illness or Injury (Relate to 24E
                      by line)
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        'A',
                        'B',
                        'C',
                        'D',
                        'E',
                        'F',
                        'G',
                        'H',
                        'I',
                        'J',
                        'K',
                        'L',
                      ].map((label, index) => (
                        <Grid item xs={6} sm={4} md={3} key={label}>
                          <TextField
                            fullWidth
                            size="small"
                            label={label}
                            value={formData.diagnosisCodes[index] || ''}
                            onChange={e =>
                              updateDiagnosisCode(index, e.target.value)
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>

                {/* Section 5: Service Details */}
                <Card sx={{ mb: 2, borderRadius: 2 }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2, color: '#1F2937' }}
                    >
                      5. Service Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="5. A. Service Start date"
                          value={parseDateToDayjs(formData.serviceStartDate)}
                          onChange={newValue => {
                            updateFormField(
                              'serviceStartDate',
                              newValue
                                ? dayjs(newValue).format('MM/DD/YYYY')
                                : ''
                            )
                          }}
                          format="MM/DD/YYYY"
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              disabled: readOnly,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="5. B. Service End Date"
                          value={parseDateToDayjs(formData.serviceEndDate)}
                          onChange={newValue => {
                            updateFormField(
                              'serviceEndDate',
                              newValue
                                ? dayjs(newValue).format('MM/DD/YYYY')
                                : ''
                            )
                          }}
                          format="MM/DD/YYYY"
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              disabled: readOnly,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="5. C. POS"
                          value={formData.pos}
                          onChange={e => updateFormField('pos', e.target.value)}
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="5. D. Procedure (comma-separated)"
                          value={formData.procedure}
                          onChange={e =>
                            updateFormField('procedure', e.target.value)
                          }
                          disabled={readOnly}
                          placeholder="e.g., 97162, 97535"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Prior Auth Number"
                          value={formData.prior_auth_number}
                          onChange={e =>
                            updateFormField('prior_auth_number', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Render Taxonomy"
                          value={formData.renderTaxonomy}
                          onChange={e =>
                            updateFormField('renderTaxonomy', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="5. G. Charges"
                          type="number"
                          value={formData.charge}
                          onChange={e =>
                            updateFormField(
                              'charge',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          disabled={readOnly}
                          InputProps={{
                            startAdornment: (
                              <Typography sx={{ mr: 1 }}>$</Typography>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="5. H. Session"
                          type="number"
                          value={formData.sessionNo}
                          onChange={e =>
                            updateFormField(
                              'sessionNo',
                              Math.max(0, parseInt(e.target.value) || 0)
                            )
                          }
                          disabled={readOnly}
                          inputProps={{ min: 0 }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Section 6-9: Financial Summary */}
                <Card sx={{ mb: 2, borderRadius: 2 }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="6. Tax ID"
                          value={formData.tax_id}
                          onChange={e =>
                            updateFormField('tax_id', e.target.value)
                          }
                          disabled={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="7. Total Charges"
                          type="number"
                          value={formData.total_charges}
                          onChange={e =>
                            updateFormField(
                              'total_charges',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          disabled={readOnly}
                          InputProps={{
                            startAdornment: (
                              <Typography sx={{ mr: 1 }}>$</Typography>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="8. Amount Paid"
                          type="number"
                          value={formData.amount_paid}
                          onChange={e =>
                            updateFormField(
                              'amount_paid',
                              Math.max(0, parseFloat(e.target.value) || 0)
                            )
                          }
                          disabled={readOnly}
                          inputProps={{ min: 0 }}
                          InputProps={{
                            startAdornment: (
                              <Typography sx={{ mr: 1 }}>$</Typography>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="9. Balance"
                          type="number"
                          value={formData.balance}
                          onChange={e =>
                            updateFormField(
                              'balance',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          disabled={readOnly}
                          InputProps={{
                            startAdornment: (
                              <Typography sx={{ mr: 1 }}>$</Typography>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Section 10 & 11: Providers */}
                <Card sx={{ mb: 2, borderRadius: 2 }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Grid container spacing={3}>
                      {/* Rendering Provider */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, mb: 2, color: '#1F2937' }}
                        >
                          10. Rendering Provider
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="First Name"
                              value={formData.rProvFistName}
                              onChange={e =>
                                updateFormField('rProvFistName', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Last Name"
                              value={formData.rProvLastName}
                              onChange={e =>
                                updateFormField('rProvLastName', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Address"
                              value={formData.rAddress}
                              onChange={e =>
                                updateFormField('rAddress', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="State"
                              value={formData.rState}
                              onChange={e =>
                                updateFormField('rState', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Phone"
                              value={formData.rPhone}
                              onChange={e =>
                                updateFormField('rPhone', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="City"
                              value={formData.rCity}
                              onChange={e =>
                                updateFormField('rCity', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Zip Code"
                              value={formData.rZip}
                              onChange={e =>
                                updateFormField('rZip', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="NPI Number"
                              value={formData.rNpi}
                              onChange={e =>
                                updateFormField('rNpi', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Bill ID"
                              value={formData.rBillid || '—'}
                              onChange={e =>
                                updateFormField('rBillid', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Billing Provider */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, mb: 2, color: '#1F2937' }}
                        >
                          11. Billing Provider
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="First Name"
                              value={formData.bProvFistName}
                              onChange={e =>
                                updateFormField('bProvFistName', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Last Name"
                              value={formData.bProvLastName}
                              onChange={e =>
                                updateFormField('bProvLastName', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Address"
                              value={formData.bAddress}
                              onChange={e =>
                                updateFormField('bAddress', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="State"
                              value={formData.bState}
                              onChange={e =>
                                updateFormField('bState', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Phone"
                              value={formData.bPhone}
                              onChange={e =>
                                updateFormField('bPhone', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="City"
                              value={formData.bCity}
                              onChange={e =>
                                updateFormField('bCity', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Zip Code"
                              value={formData.bZip}
                              onChange={e =>
                                updateFormField('bZip', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="NPI Number"
                              value={formData.bNpi}
                              onChange={e =>
                                updateFormField('bNpi', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Bill ID"
                              value={formData.bBillid || '—'}
                              onChange={e =>
                                updateFormField('bBillid', e.target.value)
                              }
                              disabled={readOnly}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: readOnly
                                    ? '#F3F4F6'
                                    : 'white',
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
        </DialogContent>
      </LocalizationProvider>

      <DialogActions sx={{ px: 4, pb: 3, gap: 2 }}>
        {readOnly || rejectAndInvoiceHave ? (
          <Button
            onClick={onClose}
            variant="contained"
            size="large"
            sx={{
              textTransform: 'none',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              onClick={onClose}
              variant="outlined"
              size="large"
              sx={{
                textTransform: 'none',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                borderColor: '#D1D5DB',
                color: '#6B7280',
                '&:hover': {
                  borderColor: '#9CA3AF',
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleGenerateClaim}
              variant="contained"
              size="large"
              disabled={loading || generating}
              startIcon={!generating && <CheckIcon />}
              sx={{
                textTransform: 'none',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                backgroundColor: '#2E7DAF',
                '&:hover': {
                  backgroundColor: '#2563a0',
                },
              }}
            >
              {generating ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  {isResubmit ? 'Resubmitting...' : 'Submitting...'}
                </>
              ) : isResubmit ? (
                'Confirm & Resubmit Claim'
              ) : (
                'Confirm & Submit Claim'
              )}
            </Button>


          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ClaimDialog
